<script setup lang="ts">
import { computed, onBeforeUnmount, ref, toRefs, watch } from "vue";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import DialogSubHeader from "@/stories/dialogs/DialogSubHeader.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";
import PixabayImageList from "@/stories/dialogs/imageDialogs/pixabay/PixabayImageList.vue";
import PixabaySearchForm from "@/stories/dialogs/imageDialogs/pixabay/PixabaySearchForm.vue";
import usePixabayDialog from "@/stories/dialogs/imageDialogs/pixabay/usePixabayDialog";
import SharedImageDialogFooter from "@/stories/dialogs/imageDialogs/SharedImageDialogFooter.vue";
import type { IFileState } from "@/interfaces";

interface Props {
  max: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  select: [IFileState[]];
}>();

const dialog = usePixabayDialog();

const { max } = toRefs(props);

const handleApply = () => {
  const selections = (dialog.images.value || []).filter((item) =>
    dialog.selectedImages.value.includes(item.id || ""),
  );
  emit("select", selections);
};

const isSelecteableMore = computed(() => {
  return dialog.selectedImages.value.length < max.value;
});

// Single-select mode: apply immediately on first selection
watch(
  dialog.selectedImages,
  (ids) => {
    if (max.value === 1 && ids.length === 1) {
      handleApply();
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  dialog.reset();
});
</script>

<template>
  <BigDialog @close="$emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("templates.online_library") }}
    </DialogHeader>

    <DialogSubHeader>
      <PixabaySearchForm />
    </DialogSubHeader>

    <DialogContent class="pixabay-dialog-content">
      <PixabayImageList :isSelecteableMore="isSelecteableMore" />
    </DialogContent>

    <SharedImageDialogFooter
      v-if="max > 1"
      :count="dialog.selectedImages.value.length"
      @close="$emit('close')"
      @apply="handleApply"
    />
  </BigDialog>
</template>
